import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { PickupTimeDisplay } from '@/components/order/PickupTimeDisplay';
import type { OrderWithItems } from '@/store/seller';
import type { OrderType } from '@/types';
import { getEffectiveOrderTime } from '@/utils/orderTime';

const orderTypeLabels: Record<OrderType, string> = {
  DEADLINE_DISCOUNT: '마감할인',
  RESERVATION_DISCOUNT: '예약할인', 
  REGULAR_ORDER: '일반주문'
};

export function DesktopOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { ToastContainer } = useToast();

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  useEffect(() => {
    const loadOrderDetail = async () => {
      if (!orderId) {
        setError('주문 ID가 없습니다');
        setLoading(false);
        return;
      }

      try {
        const result = await sellerService.getOrderDetail(orderId);
        if (result.success && result.data) {
          setOrder(result.data);
        } else {
          setError(result.error || '주문 정보를 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Order detail load error:', error);
        setError('주문 정보를 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    loadOrderDetail();
  }, [orderId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate('/seller/home');
  };

  const handleAccept = () => {
    navigate(`/seller/orders/${orderId}/accept`);
  };

  const handleReject = () => {
    navigate(`/seller/orders/${orderId}/reject`);
  };

  const handleComplete = () => {
    navigate(`/seller/orders/${orderId}/complete`);
  };

  const handleCancel = () => {
    navigate(`/seller/orders/${orderId}/cancel`);
  };

  const handleRefund = () => {
    navigate(`/seller/orders/${orderId}/refund`);
  };

  const getActionButtons = () => {
    if (!order) return null;

    switch (order.status) {
      case 'PENDING':
        return (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={handleReject}
              className="py-3 px-6 bg-orange-500 text-white rounded-lg font-medium text-lg hover:bg-orange-600 transition-colors"
            >
              주문거부
            </button>
            <button
              onClick={handleAccept}
              className="py-3 px-6 bg-green-500 text-white rounded-lg font-medium text-lg hover:bg-green-600 transition-colors"
            >
              주문수락
            </button>
          </div>
        );
      
      case 'ACCEPTED':
        return (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={handleCancel}
              className="py-3 px-6 bg-orange-500 text-white rounded-lg font-medium text-lg hover:bg-orange-600 transition-colors"
            >
              주문취소
            </button>
            <button
              onClick={handleComplete}
              className="py-3 px-6 bg-green-500 text-white rounded-lg font-medium text-lg hover:bg-green-600 transition-colors"
            >
              준비완료
            </button>
          </div>
        );
      
      case 'READY':
      case 'COMPLETED':
        return (
          <div className="max-w-sm mx-auto">
            <button
              onClick={handleRefund}
              className="w-full py-3 px-6 bg-red-500 text-white rounded-lg font-medium text-lg hover:bg-red-600 transition-colors"
            >
              환불하기
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DesktopSellerPageContainer>
        <div className="min-h-[calc(100vh-72px)] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  if (error || !order) {
    return (
      <DesktopSellerPageContainer>
        <div className="min-h-[calc(100vh-72px)] flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-gray-600 mb-4">{error || '주문 정보를 찾을 수 없습니다'}</p>
            <button
              onClick={handleBack}
              className="bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 font-medium"
            >
              홈으로 돌아가기
            </button>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-[800px] mx-auto">
        {/* 헤더 */}
        <div className="flex items-center mb-8">
          <button
            onClick={handleBack}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-[28px] font-bold text-gray-800">주문 상세</h1>
        </div>

        {/* 주문 정보 카드 */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* 좌측 컬럼 */}
            <div className="space-y-6">
              {/* 주문 유형 */}
              <div>
                <span className="text-gray-600 text-sm font-medium">주문유형</span>
                <p className="text-xl font-semibold mt-1">{orderTypeLabels[order.order_type]}</p>
              </div>

              {/* 주문자 정보 */}
              <div>
                <span className="text-gray-600 text-sm font-medium">주문자명</span>
                <p className="text-xl font-semibold mt-1">{order.buyer_nickname}</p>
              </div>

              {/* 주문번호 */}
              <div>
                <span className="text-gray-600 text-sm font-medium">주문번호</span>
                <p className="text-xl font-semibold mt-1">{order.order_number}</p>
              </div>
            </div>

            {/* 우측 컬럼 */}
            <div className="space-y-6">
              {/* 주문일시 */}
              <div>
                <span className="text-gray-600 text-sm font-medium">주문일시</span>
                <p className="text-xl font-semibold mt-1">{formatDateTime(getEffectiveOrderTime(order) || order.created_at)}</p>
              </div>

              {/* 주문방법 */}
              <div>
                <span className="text-gray-600 text-sm font-medium">주문방법</span>
                <p className="text-xl font-semibold mt-1">{orderTypeLabels[order.order_type]}</p>
              </div>

              {/* 수령일시 */}
              <div>
                <span className="text-gray-600 text-sm font-medium">수령일시</span>
                <div className="mt-1">
                  <PickupTimeDisplay 
                    order={{
                      ...order,
                      items: order.items?.map(item => ({
                        ...item,
                        product: item.products ? { id: '', name: item.products.name, original_price: 0 } : undefined
                      }))
                    } as any}
                    showDescription={true}
                    className="text-xl font-semibold text-blue-600"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 주문 상품 목록 */}
          <div className="border-t pt-8">
            <h3 className="text-[22px] font-bold mb-6">주문 상품</h3>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                  <div className="flex-1">
                    <span className="text-lg font-semibold">{item.product_name}</span>
                    <span className="text-gray-600 ml-3 text-lg">x{item.quantity}</span>
                  </div>
                  <span className="text-lg font-semibold">{formatCurrency((item.price || item.unit_price || 0) * item.quantity)}원</span>
                </div>
              ))}
            </div>
            
            {/* 총합계 */}
            <div className="border-t-2 border-gray-200 mt-6 pt-6">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold">합계</span>
                <span className="text-3xl font-bold text-green-600">
                  {formatCurrency(order.total_amount)}원
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 액션 버튼들 */}
        <div className="mb-8">
          {getActionButtons()}
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}
